import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, Link, useLocation } from 'react-router-dom';
import { 
  doc, 
  getDoc, 
  setDoc, 
  updateDoc, 
  arrayUnion, 
  arrayRemove, 
  serverTimestamp, 
  increment,
  collection,
  query,
  where,
  getDocs
} from 'firebase/firestore';
import { db } from '../../firebase';
import { useAuth } from '../../contexts/AuthContext';
import { toast } from 'react-toastify';
import { 
  FaMapMarkerAlt, 
  FaBriefcase, 
  FaMoneyBillWave, 
  FaCalendarAlt, 
  FaArrowLeft, 
  FaBuilding, 
  FaUserTie, 
  FaFileAlt,
  FaBookmark,
  FaRegBookmark,
  FaExternalLinkAlt,
  FaLink,
  FaEnvelope,
  FaPhone,
  FaGlobe,
  FaIndustry,
  FaUsers,
  FaClock,
  FaGraduationCap,
  FaCertificate,
  FaTools,
  FaInfoCircle,
  FaCheck
} from 'react-icons/fa';
import Spinner from '../../components/Spinner';

const JobDetail = () => {
  const { jobId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { currentUser } = useAuth();
  
  const [job, setJob] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isApplying, setIsApplying] = useState(false);
  const [hasApplied, setHasApplied] = useState(false);
  const [isSaved, setIsSaved] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [similarJobs, setSimilarJobs] = useState([]);
  const [company, setCompany] = useState(null);

  // Format date
  const formatDate = (timestamp) => {
    if (!timestamp) return 'Recently';
    
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  // Format experience
  const formatExperience = (min, max) => {
    if (min === 0 && max === 0) return 'Fresher';
    if (min === max) return `${min}+ years`;
    return `${min} - ${max} years`;
  };

  // Fetch job details
  const fetchJob = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      
      const jobDoc = await getDoc(doc(db, 'jobPosts', jobId));
      
      if (!jobDoc.exists()) {
        setError('Job not found');
        setLoading(false);
        return;
      }
      
      const jobData = { 
        id: jobDoc.id, 
        ...jobDoc.data(),
        postedAt: jobDoc.data().postedAt?.toDate() || jobDoc.data().createdAt?.toDate() || new Date()
      };
      
      setJob(jobData);

      // Fetch company details if available
      if (jobData.organizationId) {
        const companyDoc = await getDoc(doc(db, 'organizations', jobData.organizationId));
        if (companyDoc.exists()) {
          setCompany({
            id: companyDoc.id,
            ...companyDoc.data()
          });
        }
      }
      
      // Check if user has already applied
      if (currentUser) {
        const applicationRef = doc(db, 'applications', `${currentUser.uid}_${jobId}`);
        const applicationDoc = await getDoc(applicationRef);
        setHasApplied(applicationDoc.exists());
        
        // Check if job is saved
        const userRef = doc(db, 'users', currentUser.uid);
        const userDoc = await getDoc(userRef);
        if (userDoc.exists()) {
          const userData = userDoc.data();
          setIsSaved(userData.savedJobs?.includes(jobId) || false);
        }
      }

      // Fetch similar jobs
      if (jobData.jobTitle) {
        const jobsQuery = query(
          collection(db, 'jobPosts'),
          where('jobTitle', '==', jobData.jobTitle),
          where('id', '!=', jobId)
        );
        
        const querySnapshot = await getDocs(jobsQuery);
        const similar = [];
        querySnapshot.forEach((doc) => {
          similar.push({ id: doc.id, ...doc.data() });
        });
        setSimilarJobs(similar.slice(0, 3)); // Limit to 3 similar jobs
      }
      
    } catch (err) {
      console.error('Error fetching job:', err);
      setError('Failed to load job details');
      toast.error('Failed to load job details');
    } finally {
      setLoading(false);
    }
  }, [jobId, currentUser]);
  
  useEffect(() => {
    fetchJob();
  }, [fetchJob]);

  // Handle job application
  const handleApply = async () => {
    if (!currentUser) {
      toast.info('Please sign in to apply for this job');
      navigate('/auth', { state: { from: location.pathname } });
      return;
    }

    try {
      setIsApplying(true);
      
      if (hasApplied) {
        toast.info('You have already applied to this job');
        return;
      }

      const applicationId = `${currentUser.uid}_${jobId}`;
      const applicationData = {
        jobId,
        jobTitle: job.jobTitle || job.title,
        companyName: job.organizationName || job.companyName,
        companyId: job.organizationId,
        jobLocation: job.location,
        jobType: job.jobType,
        salary: job.salary,
        status: 'applied',
        appliedAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        applicationDate: new Date().toISOString(),
        
        // Applicant details
        applicantId: currentUser.uid,
        applicantName: currentUser.displayName || 'Anonymous',
        applicantEmail: currentUser.email,
        phoneNumber: currentUser.phoneNumber || '',
        resume: currentUser.resumeUrl || '',
        
        // Status tracking
        viewed: false,
        shortlisted: false,
        interviewScheduled: false,
        rejected: false,
        
        // Analytics
        source: 'job_detail_page',
        ipAddress: '', // Can be captured on the server side
        userAgent: navigator.userAgent
      };

      // Create application document
      await setDoc(doc(db, 'applications', applicationId), applicationData);
      
      // Update job's application count
      await updateDoc(doc(db, 'jobPosts', jobId), {
        applications: increment(1),
        updatedAt: serverTimestamp()
      });
      
      // Add to user's applied jobs
      const userRef = doc(db, 'users', currentUser.uid);
      await updateDoc(userRef, {
        appliedJobs: arrayUnion(jobId),
        updatedAt: serverTimestamp()
      });
      
      // Send notification to employer
      if (job.organizationId) {
        const notificationRef = doc(collection(db, 'notifications'));
        await setDoc(notificationRef, {
          type: 'new_application',
          recipientId: job.organizationId,
          senderId: currentUser.uid,
          senderName: currentUser.displayName || 'An applicant',
          jobId: jobId,
          jobTitle: job.jobTitle || job.title,
          applicationId: applicationId,
          read: false,
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp()
        });
      }
      
      setHasApplied(true);
      toast.success('Application submitted successfully!');
      
    } catch (error) {
      console.error('Error applying to job:', error);
      toast.error('Failed to submit application. Please try again.');
    } finally {
      setIsApplying(false);
    }
  };

  // Toggle save job
  const toggleSaveJob = async () => {
    if (!currentUser) {
      toast.info('Please sign in to save jobs');
      navigate('/auth', { state: { from: location.pathname } });
      return;
    }

    try {
      setSaving(true);
      const userRef = doc(db, 'users', currentUser.uid);
      
      if (isSaved) {
        await updateDoc(userRef, {
          savedJobs: arrayRemove(jobId),
          updatedAt: serverTimestamp()
        });
        setIsSaved(false);
        toast.success('Job removed from saved jobs');
      } else {
        await updateDoc(userRef, {
          savedJobs: arrayUnion(jobId),
          updatedAt: serverTimestamp()
        });
        setIsSaved(true);
        toast.success('Job saved successfully');
        
        // Track saved job event
        const saveRef = collection(db, 'savedJobs');
        await setDoc(doc(saveRef, `${currentUser.uid}_${jobId}`), {
          userId: currentUser.uid,
          jobId: jobId,
          jobTitle: job.jobTitle || job.title,
          companyName: job.organizationName || job.companyName,
          savedAt: serverTimestamp(),
          source: 'job_detail_page'
        });
      }
      
    } catch (error) {
      console.error('Error saving job:', error);
      toast.error('Failed to update saved jobs');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <Spinner size="lg" />
      </div>
    );
  }

  if (error || !job) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center p-6 max-w-md">
          <h2 className="text-2xl font-semibold text-gray-800 mb-3">
            {error || 'Job Not Found'}
          </h2>
          <p className="text-gray-600 mb-6">
            {error || "The job you're looking for doesn't exist or has been removed."}
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Link 
              to="/app/dashboard/professional" 
              className="inline-flex items-center justify-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              <FaArrowLeft className="mr-2" /> Back to Dashboard
            </Link>
            <Link 
              to="/jobs" 
              className="inline-flex items-center justify-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              Browse All Jobs
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4 sm:px-6 lg:px-8">
      <div className="max-w-7xl mx-auto">
        {/* Back button */}
        <div className="mb-6">
          <button
            onClick={() => navigate(-1)}
            className="flex items-center text-blue-600 hover:text-blue-800 text-sm font-medium"
          >
            <FaArrowLeft className="mr-2" /> Back to Jobs
          </button>
        </div>

        {/* Main Content */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Job Details */}
          <div className="lg:col-span-2 space-y-6">
            {/* Job Header */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                <div>
                  <h1 className="text-2xl font-bold text-gray-900">{job.jobTitle || 'Job Title'}</h1>
                  <div className="mt-1 flex flex-wrap items-center gap-2 text-gray-600">
                    {job.organizationName && (
                      <span className="flex items-center">
                        <FaBuilding className="mr-1.5 text-gray-400" />
                        {job.organizationName}
                      </span>
                    )}
                    {job.location && (
                      <span className="flex items-center">
                        <FaMapMarkerAlt className="mr-1.5 text-gray-400" />
                        {job.location}
                      </span>
                    )}
                    {job.jobType && (
                      <span className="flex items-center">
                        <FaClock className="mr-1.5 text-gray-400" />
                        {job.jobType}
                      </span>
                    )}
                  </div>
                </div>
                <div className="flex flex-col sm:flex-row gap-3 w-full sm:w-auto">
                  <button
                    onClick={toggleSaveJob}
                    disabled={saving}
                    className={`flex items-center justify-center px-4 py-2 border rounded-md text-sm font-medium ${
                      isSaved 
                        ? 'bg-yellow-50 border-yellow-200 text-yellow-700 hover:bg-yellow-100' 
                        : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'
                    }`}
                  >
                    {saving ? (
                      <>
                        <Spinner size="sm" className="mr-2" />
                        {isSaved ? 'Removing...' : 'Saving...'}
                      </>
                    ) : (
                      <>
                        {isSaved ? (
                          <FaBookmark className="mr-2 text-yellow-500" />
                        ) : (
                          <FaRegBookmark className="mr-2" />
                        )}
                        {isSaved ? 'Saved' : 'Save Job'}
                      </>
                    )}
                  </button>
                  <button
                    onClick={handleApply}
                    disabled={isApplying || hasApplied}
                    className={`flex items-center justify-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white ${
                      hasApplied 
                        ? 'bg-green-600 hover:bg-green-700' 
                        : 'bg-blue-600 hover:bg-blue-700'
                    }`}
                  >
                    {isApplying ? (
                      <>
                        <Spinner size="sm" className="mr-2" />
                        Applying...
                      </>
                    ) : hasApplied ? (
                      <>
                        <FaCheck className="mr-2" />
                        Applied
                      </>
                    ) : (
                      <>
                        <FaFileAlt className="mr-2" />
                        Apply Now
                      </>
                    )}
                  </button>
                </div>
              </div>
              
              {/* Job Highlights */}
              <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                {job.salary && (
                  <div className="bg-blue-50 p-3 rounded-lg">
                    <div className="flex items-center">
                      <FaMoneyBillWave className="text-blue-500 mr-2" />
                      <div>
                        <p className="text-xs text-gray-500">SALARY</p>
                        <p className="font-medium text-gray-900">{job.salary}</p>
                      </div>
                    </div>
                  </div>
                )}
                
                {(job.experienceMin !== undefined || job.experienceMax !== undefined) && (
                  <div className="bg-blue-50 p-3 rounded-lg">
                    <div className="flex items-center">
                      <FaBriefcase className="text-blue-500 mr-2" />
                      <div>
                        <p className="text-xs text-gray-500">EXPERIENCE</p>
                        <p className="font-medium text-gray-900">
                          {formatExperience(job.experienceMin, job.experienceMax)}
                        </p>
                      </div>
                    </div>
                  </div>
                )}
                
                {job.vacancies && (
                  <div className="bg-blue-50 p-3 rounded-lg">
                    <div className="flex items-center">
                      <FaUsers className="text-blue-500 mr-2" />
                      <div>
                        <p className="text-xs text-gray-500">VACANCIES</p>
                        <p className="font-medium text-gray-900">{job.vacancies}</p>
                      </div>
                    </div>
                  </div>
                )}
                
                {job.applyBy && (
                  <div className="bg-blue-50 p-3 rounded-lg">
                    <div className="flex items-center">
                      <FaCalendarAlt className="text-blue-500 mr-2" />
                      <div>
                        <p className="text-xs text-gray-500">APPLY BEFORE</p>
                        <p className="font-medium text-gray-900">
                          {formatDate(job.applyBy)}
                        </p>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Job Description */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                <FaFileAlt className="mr-2 text-blue-600" />
                Job Description
              </h2>
              <div className="prose max-w-none text-gray-700">
                {job.jobDescription ? (
                  <div dangerouslySetInnerHTML={{ __html: job.jobDescription.replace(/\n/g, '<br />') }} />
                ) : (
                  <p className="text-gray-500 italic">No job description provided.</p>
                )}
              </div>
            </div>

            {/* Key Responsibilities */}
            {job.responsibilities && job.responsibilities.length > 0 && (
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                <h2 className="text-lg font-semibold text-gray-900 mb-4">
                  Key Responsibilities
                </h2>
                <ul className="list-disc pl-5 space-y-2 text-gray-700">
                  {job.responsibilities.map((item, index) => (
                    <li key={index}>{item}</li>
                  ))}
                </ul>
              </div>
            )}

            {/* Skills & Qualifications */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                <FaTools className="mr-2 text-blue-600" />
                Skills & Qualifications
              </h2>
              
              <div className="space-y-4">
                {job.skills && job.skills.length > 0 && (
                  <div>
                    <h3 className="font-medium text-gray-800 mb-2">Required Skills:</h3>
                    <div className="flex flex-wrap gap-2">
                      {job.skills.map((skill, index) => (
                        <span 
                          key={index} 
                          className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-blue-100 text-blue-800"
                        >
                          {skill}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {job.qualifications && job.qualifications.length > 0 && (
                  <div>
                    <h3 className="font-medium text-gray-800 mb-2">Qualifications:</h3>
                    <ul className="list-disc pl-5 space-y-1 text-gray-700">
                      {job.qualifications.map((qualification, index) => (
                        <li key={index}>{qualification}</li>
                      ))}
                    </ul>
                  </div>
                )}

                {job.education && (
                  <div>
                    <h3 className="font-medium text-gray-800 mb-2 flex items-center">
                      <FaGraduationCap className="mr-1.5 text-blue-600" />
                      Education
                    </h3>
                    <p className="text-gray-700">{job.education}</p>
                  </div>
                )}
              </div>
            </div>

            {/* About Company */}
            {job.aboutCompany && (
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                <h2 className="text-lg font-semibold text-gray-900 mb-4">
                  About {job.organizationName || 'the Company'}
                </h2>
                <div className="prose max-w-none text-gray-700">
                  {job.aboutCompany}
                </div>
              </div>
            )}
          </div>

          {/* Sidebar */}
          <div className="space-y-4">
            {/* Job Summary */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">
                Job Summary
              </h2>
              <div className="space-y-3 text-sm">
                {job.jobType && (
                  <div className="flex justify-between">
                    <span className="text-gray-600">Job Type:</span>
                    <span className="font-medium text-gray-900">{job.jobType}</span>
                  </div>
                )}
                {job.location && (
                  <div className="flex justify-between">
                    <span className="text-gray-600">Location:</span>
                    <span className="font-medium text-gray-900">{job.location}</span>
                  </div>
                )}
                {job.experienceMin !== undefined && (
                  <div className="flex justify-between">
                    <span className="text-gray-600">Experience:</span>
                    <span className="font-medium text-gray-900">
                      {formatExperience(job.experienceMin, job.experienceMax || job.experienceMin)}
                    </span>
                  </div>
                )}
                {job.salary && (
                  <div className="flex justify-between">
                    <span className="text-gray-600">Salary:</span>
                    <span className="font-medium text-gray-900">{job.salary}</span>
                  </div>
                )}
                {job.vacancies && (
                  <div className="flex justify-between">
                    <span className="text-gray-600">Vacancies:</span>
                    <span className="font-medium text-gray-900">{job.vacancies}</span>
                  </div>
                )}
                {job.postedAt && (
                  <div className="flex justify-between">
                    <span className="text-gray-600">Posted:</span>
                    <span className="font-medium text-gray-900">
                      {formatDate(job.postedAt || job.createdAt)}
                    </span>
                  </div>
                )}
                {job.applyBy && (
                  <div className="flex justify-between">
                    <span className="text-gray-600">Apply Before:</span>
                    <span className="font-medium text-gray-900">
                      {formatDate(job.applyBy)}
                    </span>
                  </div>
                )}
              </div>
            </div>

            {/* Company Info */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">
                Company Information
              </h2>
              <div className="space-y-3">
                <div>
                  <h3 className="font-medium text-gray-900">
                    {job.organizationName || 'Company Name'}
                  </h3>
                  {job.companyWebsite && (
                    <a 
                      href={job.companyWebsite.startsWith('http') ? job.companyWebsite : `https://${job.companyWebsite}`}
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="text-blue-600 hover:underline text-sm flex items-center"
                    >
                      {job.companyWebsite.replace(/^https?:\/\//, '')}
                      <FaExternalLinkAlt className="ml-1 text-xs" />
                    </a>
                  )}
                </div>
                
                {job.companySize && (
                  <div className="text-sm">
                    <div className="text-gray-600">Company Size:</div>
                    <div className="text-gray-900">{job.companySize}</div>
                  </div>
                )}
                
                {job.companyIndustry && (
                  <div className="text-sm">
                    <div className="text-gray-600">Industry:</div>
                    <div className="text-gray-900">{job.companyIndustry}</div>
                  </div>
                )}
                
                {company?.email && (
                  <div className="text-sm">
                    <div className="text-gray-600">Email:</div>
                    <a 
                      href={`mailto:${company.email}`}
                      className="text-blue-600 hover:underline flex items-center"
                    >
                      {company.email}
                      <FaEnvelope className="ml-1 text-xs" />
                    </a>
                  </div>
                )}
                
                {company?.phone && (
                  <div className="text-sm">
                    <div className="text-gray-600">Phone:</div>
                    <a 
                      href={`tel:${company.phone}`}
                      className="text-blue-600 hover:underline flex items-center"
                    >
                      {company.phone}
                      <FaPhone className="ml-1 text-xs" />
                    </a>
                  </div>
                )}
              </div>
            </div>

            {/* Application Tips */}
            <div className="bg-blue-50 border border-blue-100 rounded-lg p-4 text-sm text-blue-700">
              <div className="flex">
                <FaInfoCircle className="mr-2 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="font-medium">Application Tips</p>
                  <p className="mt-1">
                    Make sure your profile is complete and up-to-date before applying. 
                    Tailor your application to highlight relevant experience and skills.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Similar Jobs */}
        {similarJobs.length > 0 && (
          <div className="mt-12">
            <h2 className="text-xl font-semibold text-gray-900 mb-6">
              Similar Jobs
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {similarJobs.map((similarJob) => (
                <div 
                  key={similarJob.id}
                  className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 hover:shadow-md transition-shadow cursor-pointer"
                  onClick={() => navigate(`/jobs/${similarJob.id}`)}
                >
                  <h3 className="text-lg font-medium text-gray-900 mb-1">
                    {similarJob.jobTitle || similarJob.title}
                  </h3>
                  <p className="text-gray-600 text-sm mb-3">
                    {similarJob.organizationName || similarJob.companyName}
                  </p>
                  <div className="flex flex-wrap gap-2 text-xs">
                    {similarJob.location && (
                      <span className="flex items-center text-gray-500">
                        <FaMapMarkerAlt className="mr-1" />
                        {similarJob.location}
                      </span>
                    )}
                    {similarJob.jobType && (
                      <span className="flex items-center text-gray-500">
                        <FaBriefcase className="mr-1" />
                        {similarJob.jobType}
                      </span>
                    )}
                    {similarJob.salary && (
                      <span className="flex items-center text-gray-500">
                        <FaMoneyBillWave className="mr-1" />
                        {similarJob.salary}
                      </span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default JobDetail;
